// @ts-nocheck
/**
 * Shipping quote form — two-step wizard + Shopify contact fallback + custom API hook.
 */
class QuoteFormWizard extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;

    this.twoStep = this.dataset.twoStep === 'true';
    this.currentStep = 1;
    this.steps = this.querySelectorAll('[data-quote-step]');
    this.stepIndicators = this.querySelectorAll('[data-step-indicator]');
    this.continueBtn = this.querySelector('[data-quote-continue]');
    this.backBtn = this.querySelector('[data-quote-back]');
    this.submitBtn = this.querySelector('[data-quote-submit]');
    this.alertEl = this.querySelector('[data-quote-alert]');
    this.bodyField = this.form.querySelector('[name="contact[body]"]');
    this.phoneInput = this.form.querySelector('[data-phone-input]');
    this.phoneCountry = this.form.querySelector('[data-phone-country]');
    this.phoneE164Input = this.form.querySelector('[data-phone-e164]');

    if (this.continueBtn) {
      this.continueBtn.addEventListener('click', () => this.goToStep(2));
    }
    if (this.backBtn) {
      this.backBtn.addEventListener('click', () => this.goToStep(1));
    }

    this.form.addEventListener('submit', (event) => this.onSubmit(event));
    this.setupPhoneField();
    this.setupZipFields();
    this.updateStepUI();
  }

  setupZipFields() {
    this.form.querySelectorAll('[name="quote_delivery_zip"]').forEach((field) => {
      field.addEventListener('input', () => {
        field.value = field.value.replace(/\D/g, '').slice(0, 5);
        field.classList.remove('quote-form-field-error');
      });
    });
  }

  setupPhoneField() {
    if (!this.phoneInput || !this.phoneCountry || !this.phoneE164Input) return;

    window.PSPhoneField?.populateCountryOptions(this.phoneCountry);

    this.phoneInput.addEventListener('input', () => {
      this.phoneInput.classList.remove('quote-form-field-error');
      this.syncPhoneValue(false);
    });
    this.phoneCountry.addEventListener('change', () => {
      this.phoneInput.classList.remove('quote-form-field-error');
      this.syncPhoneValue(false);
    });
  }

  goToStep(step) {
    if (step === 2 && this.twoStep && !this.validateStep(1)) return;
    this.currentStep = step;
    this.updateStepUI();
    this.clearAlert();
    this.scrollToForm();
  }

  updateStepUI() {
    this.steps.forEach((el) => {
      const step = Number(el.dataset.quoteStep);
      if (this.twoStep) {
        el.hidden = step !== this.currentStep;
      } else {
        el.hidden = false;
      }
    });

    this.stepIndicators.forEach((el) => {
      const step = Number(el.dataset.stepIndicator);
      el.classList.toggle('is-active', step === this.currentStep);
      el.classList.toggle('is-complete', step < this.currentStep);
    });

    if (this.continueBtn) this.continueBtn.hidden = !this.twoStep || this.currentStep !== 1;
    if (this.backBtn) this.backBtn.hidden = !this.twoStep || this.currentStep !== 2;
    if (this.submitBtn) this.submitBtn.hidden = this.twoStep && this.currentStep !== 2;
  }

  isValidZip(value) {
    return /^\d{5}$/.test(value.trim());
  }

  validateStep(step) {
    const panel = this.querySelector(`[data-quote-step="${step}"]`);
    if (!panel) return true;

    let valid = true;
    let zipInvalid = false;

    panel.querySelectorAll('input:not([type="radio"]):not([type="hidden"]), select, textarea').forEach((field) => {
      if (!field.required) return;

      const isZip = field.name === 'quote_delivery_zip';
      const value = field.value.trim();
      const fieldValid = isZip ? this.isValidZip(value) : Boolean(value);

      if (!fieldValid) {
        valid = false;
        field.classList.add('quote-form-field-error');
        if (isZip) zipInvalid = true;
      } else {
        field.classList.remove('quote-form-field-error');
      }
    });

    ['quote_liftgate', 'quote_residential'].forEach((name) => {
      if (!panel.querySelector(`[name="${name}"]`)) return;
      if (!panel.querySelector(`[name="${name}"]:checked`)) valid = false;
    });

    if (!valid) {
      const zipMessage = this.dataset.zipMessage || 'Please enter a 5-digit ZIP code.';
      const requiredMessage = this.dataset.validationMessage || 'Please fill in all required fields.';
      this.showAlert(zipInvalid ? zipMessage : requiredMessage, 'error');
    }

    return valid;
  }

  validateAll() {
    return this.validateStep(1) && this.validateStep(2);
  }

  syncPhoneValue(markInvalid) {
    return window.PSPhoneField?.syncValue({
      phoneInput: this.phoneInput,
      phoneCountry: this.phoneCountry,
      phoneE164Input: this.phoneE164Input,
      markInvalid,
      errorClass: 'quote-form-field-error'
    }) ?? { valid: false, e164: '' };
  }

  scrollToForm() {
    const card = this.closest('[class*="quote-form-card"]') || this;
    card.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  collectData() {
    const get = (name) => this.form.querySelector(`[name="${name}"]`)?.value?.trim() || '';
    const getRadio = (name) => this.form.querySelector(`[name="${name}"]:checked`)?.value || '';

    return {
      interest: get('quote_interest'),
      pallet_count: get('quote_pallet_count'),
      details: get('quote_details'),
      delivery_zip: get('quote_delivery_zip'),
      liftgate: getRadio('quote_liftgate'),
      residential: getRadio('quote_residential'),
      name: get('contact[name]'),
      email: get('contact[email]'),
      phone: this.phoneE164Input?.value || get('contact[phone]'),
      business_name: get('quote_business_name')
    };
  }

  requestTarget() {
    if (this.dataset.useEcommerceApi === 'true') {
      const api = window.PSEcommerceApi;
      if (!api) return null;
      return {
        endpoint: api.url('/v1/storefront/request-shipping-quote'),
        headers: api.headers()
      };
    }

    if (!this.dataset.apiEndpoint) return null;

    return {
      endpoint: this.dataset.apiEndpoint,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'x-api-key': 'uoirweuorudlfjlsd'
      }
    };
  }

  buildShopifyBody(data) {
    return [
      '--- Shipping Quote Request ---',
      '',
      'About Your Request:',
      `Interest: ${data.interest}`,
      `Pallet count: ${data.pallet_count}`,
      data.details ? `Details: ${data.details}` : '',
      '',
      'Shipping Information:',
      `Delivery ZIP: ${data.delivery_zip}`,
      `Liftgate required: ${data.liftgate}`,
      `Residential delivery: ${data.residential}`,
      '',
      'Customer Information:',
      `Business name: ${data.business_name || 'N/A'}`
    ].filter(Boolean).join('\n');
  }

  async onSubmit(event) {
    const useApi = this.dataset.useCustomApi === 'true';

    if (useApi) {
      event.preventDefault();
    }

    if (this.twoStep && this.currentStep === 1) {
      event.preventDefault();
      this.goToStep(2);
      return;
    }

    const isValid = this.twoStep ? this.validateStep(2) : this.validateAll();
    if (!isValid) {
      event.preventDefault();
      return;
    }

    const phoneState = this.syncPhoneValue(true);
    if (!phoneState.valid) {
      event.preventDefault();
      this.showAlert('Please enter a valid phone number for the selected country.', 'error');
      return;
    }

    const data = this.collectData();
    const target = this.requestTarget();

    if (useApi) {
      if (!target) {
        this.showAlert('API endpoint is not configured yet.', 'error');
        return;
      }

      if (this.submitBtn) this.submitBtn.disabled = true;
      let redirected = false;

      try {
        const response = await fetch(target.endpoint, {
          method: 'POST',
          headers: target.headers,
          body: JSON.stringify(data)
        });
        if (!response.ok) throw new Error('Request failed');

        const thankYouUrl = this.dataset.thankYouUrl || '/pages/thank-you';
        const separator = thankYouUrl.includes('?') ? '&' : '?';
        redirected = true;
        window.location.replace(`${thankYouUrl}${separator}source=shipping-quote`);
        return;
      } catch (error) {
        this.showAlert(this.dataset.errorMessage || 'Something went wrong. Please try again.', 'error');
      } finally {
        if (!redirected && this.submitBtn) this.submitBtn.disabled = false;
      }
      return;
    }

    if (this.bodyField) {
      this.bodyField.value = this.buildShopifyBody(data);
    }
  }

  showAlert(text, type) {
    if (!this.alertEl) return;
    this.alertEl.hidden = false;
    this.alertEl.textContent = text;
    this.alertEl.className = `quote-form-alert quote-form-alert--${type}`;
    this.alertEl.setAttribute('role', 'alert');
    this.alertEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  clearAlert() {
    if (!this.alertEl) return;
    this.alertEl.hidden = true;
    this.alertEl.textContent = '';
    this.alertEl.className = 'quote-form-alert';
  }
}

if (!customElements.get('quote-form-wizard')) {
  customElements.define('quote-form-wizard', QuoteFormWizard);
}
